import {
  Box,
  Grid,
  InputLabel,
  MenuItem,
  ListItemText,
  Select,
  Checkbox,
  FormControl,
  FormLabel,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useContext, useEffect, useState } from "react";
import { COVERAGE_FORM } from "@platform/rfp-core";
import { TextField, Typography } from "@platform/design-system";

import { CommissionSection, SectionTitle } from "../../components";
import { RFP_ACTION_TYPES, STATE_OPTIONS } from "../../constants";
import { handleNumericFieldChange } from "../utils";
import { RfpContext } from "../../contexts/rfpContext";
import CoverageButtonWrapper from "../components/coverageButtonWrapper";
import CommentsField from "../components/commentsField";

import { createValidationHandlers } from "./coveragesValidations";

const {
  PLAN_TYPE,
  ENROLLED_LIVES,
  PLANS,
  ALTERNATE_PLANS,
  ADDITIONAL_NOTES,
  CURRENT_COMMISSION,
  REQUESTED_COMMISSION,
  SINGLE_PLAN,
  STATES_KEY,
  NO_PLAN,
} = COVERAGE_FORM;

const STATES_OPTIONS_KEY = Object.keys(STATE_OPTIONS);

const { UPDATE_SELECTED_COVERAGE } = RFP_ACTION_TYPES;

const MenuProps = {
  PaperProps: {
    style: {
      width: 250,
    },
  },
};

const initialValues = {
  [STATES_KEY]: {},
  [CURRENT_COMMISSION]: "",
  [REQUESTED_COMMISSION]: "",
  [ADDITIONAL_NOTES]: "",
  [ALTERNATE_PLANS]: "",
  [ENROLLED_LIVES]: "",
};

export const initialSDLCoveragesDetails = {
  [PLAN_TYPE]: SINGLE_PLAN,
  [PLANS]: [
    {
      ...initialValues,
      name: SINGLE_PLAN,
      states: [],
    },
  ],
};

const StateDisabilityLeaves = ({
  coverage,
  handleNext,
  isLastCoverage,
  nextCoverage,
  handleBack,
  prevCoverage,
  isFirstCoverage,
}) => {
  const { setRfp } = useContext(RfpContext);

  const handleFormChange = (updatedCoverage, currentPlanKey, fieldKey) => {
    setRfp({
      type: UPDATE_SELECTED_COVERAGE,
      value: updatedCoverage,
      currentPlanKey,
      fieldKey,
    });
  };

  return (
    <>
      <StateDisabilityLeavesForm
        coverage={coverage}
        onFormChange={handleFormChange}
      />
      <CoverageButtonWrapper
        displayBackButton={!isFirstCoverage}
        displayNextButton={!isLastCoverage}
        backButtonName={prevCoverage?.name}
        nextButtonName={nextCoverage?.name}
        handleBackAction={handleBack}
        handleNextAction={handleNext}
      />
    </>
  );
};

export const StateDisabilityLeavesForm = ({ coverage, onFormChange }) => {
  const isCurrentCoverage = coverage?.isCurrentCoverage;
  const currentPlan = coverage?.details[PLANS][0];
  const currentPlanErrors = currentPlan.errors || {};

  const handleMultipleSelectChange = (event) => {
    const {
      target: { value },
    } = event;
    const newStates = typeof value === "string" ? value.split(",") : value;
    newStates.forEach((s) => handleChangeStateOption({ stateName: s }));
  };

  const handleDelete = async (name) => {
    const newStates = { ...(currentPlan[STATES_KEY] || {}) };
    delete newStates[name];
    handlePlanFormChange({ name: STATES_KEY, value: newStates });
  };

  const handleChangeStateOption = ({ stateName, optionName }) => {
    const localStates = { ...(currentPlan[STATES_KEY] || {}) };

    if (localStates[stateName]) {
      var arrayOptions = localStates[stateName];
      if (arrayOptions.includes(optionName)) {
        arrayOptions = arrayOptions.filter((v) => v != optionName);
        if (arrayOptions.length === 0) {
          delete localStates[stateName];
        } else {
          localStates[stateName] = arrayOptions;
        }
      } else {
        arrayOptions.push(optionName);
        localStates[stateName] = arrayOptions;
      }
      handlePlanFormChange({ name: STATES_KEY, value: localStates });
    } else {
      const newStates = { ...localStates, [stateName]: [optionName] };
      handlePlanFormChange({ name: STATES_KEY, value: newStates });
    }
  };

  const handlePlanFormChange = ({ name, value }) => {
    const localPlan = {
      ...currentPlan,
      [name]: value,
      displayName: NO_PLAN,
    };
    const payload = {
      ...coverage,
      details: { ...coverage.details, [PLANS]: [localPlan] },
    };

    onFormChange(payload, 0, name);
  };

  const { handlePlanFormChangeWithValidation, handlePreviousFieldsValidation } =
    createValidationHandlers({
      currentPlan,
      coverage,
      onFormChange,
      isCurrentCoverage,
      activeTab: 0,
      planName: SINGLE_PLAN,
    });

  return (
    <>
      <SectionTitle>Enrolled Lives</SectionTitle>
      <Grid container spacing={2} px={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            autoComplete="off"
            fullWidth
            name={ENROLLED_LIVES}
            error={!!currentPlanErrors[ENROLLED_LIVES]}
            value={currentPlan[ENROLLED_LIVES] || ""}
            onChange={(e) => {
              handleNumericFieldChange(
                e.target,
                SINGLE_PLAN,
                handlePlanFormChange,
              );
            }}
            onFocus={() => {
              handlePreviousFieldsValidation(ENROLLED_LIVES);
            }}
            onBlur={(e) => {
              handlePlanFormChangeWithValidation(e.target);
            }}
            label="Number of Enrolled Members"
            required
          />
          {!!currentPlanErrors && (
            <Typography variant="caption" color="danger">
              {currentPlanErrors[ENROLLED_LIVES]}
            </Typography>
          )}
        </Grid>
      </Grid>

      <SectionTitle>State Selection</SectionTitle>
      <Grid container spacing={2} px={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl sx={{ width: "100%" }}>
            <InputLabel id="state-multiple-checkbox-label" required>
              States
            </InputLabel>
            <Select
              error={!!currentPlanErrors[STATES_KEY]}
              labelId="state-multiple-checkbox-label"
              id="state-multiple-checkbox"
              multiple
              name={STATES_KEY}
              value={[...Object.keys(currentPlan[STATES_KEY] || {})]}
              onChange={handleMultipleSelectChange}
              onFocus={() => {
                handlePreviousFieldsValidation(STATES_KEY);
              }}
              onBlur={(e) => {
                handlePlanFormChangeWithValidation(e.target);
              }}
              renderValue={(selected) => selected.join(", ")}
              MenuProps={MenuProps}
              label="States"
              required
            >
              {STATES_OPTIONS_KEY.map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox checked={!!currentPlan[STATES_KEY]?.[name]} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box>
            {typeof currentPlanErrors[STATES_KEY] === "string" && (
              <Typography variant="caption" color="danger">
                {currentPlanErrors[STATES_KEY]}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      {Object.keys(currentPlan[STATES_KEY] || {}).map((stateName) => {
        const currentStates = currentPlan[STATES_KEY] || {};
        return (
          <Box key={stateName} mt={1.5} pl={2} pr={2}>
            <FormControl>
              <FormLabel id={`${stateName}-structure-label`}>
                {`${stateName} Coverages`}
                {"  "}
                <span className={"required"}>*</span>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(stateName)}
                >
                  <CloseIcon />
                </IconButton>
              </FormLabel>
              <Grid mx={1} container>
                {STATE_OPTIONS[stateName].map((option) => (
                  <StateOption
                    key={option}
                    stateName={stateName}
                    option={option}
                    handleChangeStateOption={handleChangeStateOption}
                    isOneOption={STATE_OPTIONS[stateName].length === 1}
                    isPreSelected={currentStates[stateName]?.includes(option)}
                  />
                ))}
              </Grid>
              <Box>
                {!!currentPlanErrors[STATES_KEY] && (
                  <Typography variant="caption" color="danger">
                    {currentPlanErrors[STATES_KEY][stateName]}
                  </Typography>
                )}
              </Box>
            </FormControl>
          </Box>
        );
      })}

      <CommissionSection
        handlePlanFormChange={handlePlanFormChange}
        handlePlanFormChangeWithValidation={handlePlanFormChangeWithValidation}
        handlePreviousFieldsValidation={handlePreviousFieldsValidation}
        currentPlanErrors={currentPlanErrors}
        currentPlan={currentPlan}
        planName={SINGLE_PLAN}
        isCurrentCoverage={isCurrentCoverage}
      />

      <CommentsField
        currentPlan={currentPlan}
        onChange={(e) => {
          handlePlanFormChange(e.target);
        }}
        handlePreviousFieldsValidation={handlePreviousFieldsValidation}
      />
    </>
  );
};

const StateOption = ({
  option,
  stateName,
  handleChangeStateOption,
  isOneOption,
  isPreSelected,
}) => {
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    if (isOneOption && !isPreSelected) {
      setChecked(true);
      handleChangeStateOption({ optionName: option, stateName });
    }
  }, [isOneOption, isPreSelected, handleChangeStateOption, option, stateName]);

  useEffect(() => {
    if (isPreSelected) {
      setChecked(true);
    }
  }, [isPreSelected]);

  return (
    <FormControlLabel
      key={option}
      name={option}
      value={option || ""}
      control={<Checkbox />}
      label={option}
      checked={checked}
      onChange={() => {
        if (!isOneOption) {
          setChecked(!checked);
          handleChangeStateOption({ optionName: option, stateName });
        }
      }}
    />
  );
};

export default StateDisabilityLeaves;
